/**
 * MigrateInvoicesStep - Étape onboarding pour importer des factures existantes
 *
 * Réutilise BatchUploadZone + BatchReviewLayout + useBatchOCR.
 * Design System: Landing/Auth context (gradient teal, text-white pour le contenu onboarding)
 * mais le contenu des cartes est en Dashboard context (text-gray-900).
 */

import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, SkipForward } from 'lucide-react';
import { BatchUploadZone } from '@/features/expenses/components/BatchUploadZone';
import { BatchReviewLayout } from '@/features/expenses/components/BatchReviewLayout';
import { useBatchOCR } from '@/features/expenses/hooks/useBatchOCR';
import { useBatchReview } from '@/features/expenses/hooks/useBatchReview';
import { useBatchImportInvoices, type BatchInvoiceData, type BatchInvoiceLineItem } from '@/features/billing/hooks/useBatchImportInvoices';
import { Building2, Calendar, Euro, Hash } from 'lucide-react';

function extractLineItems(ocrData: Record<string, unknown>): BatchInvoiceLineItem[] | undefined {
  const rawLines = ocrData.lines || ocrData.line_items || ocrData.lineItems;
  if (!Array.isArray(rawLines) || rawLines.length === 0) return undefined;
  return rawLines
    .filter((line: Record<string, unknown>) => line.description || line.label)
    .map((line: Record<string, unknown>) => ({
      description: (line.description || line.label || '') as string,
      quantity: Number(line.quantity || line.qty || 1),
      unitPrice: Number(line.unit_price || line.unitPrice || line.price || 0),
      taxPercent: Number(line.tax_percent || line.taxPercent || line.vat || 20),
    }));
}

function extractInvoiceData(ocrData: Record<string, unknown> | null, filename: string): BatchInvoiceData {
  if (!ocrData) {
    return { clientName: '', invoiceNumber: '', issueDate: new Date().toISOString().split('T')[0], totalHT: 0, taxRate: 20, status: 'PAID' };
  }
  const client = ocrData.client || ocrData.customer;
  const clientObj = typeof client === 'object' && client !== null ? client as Record<string, unknown> : null;
  return {
    clientName: clientObj?.name as string || clientObj?.company_name as string || ocrData.clientName as string || '',
    clientSiret: clientObj?.siret as string || ocrData.clientSiret as string || '',
    invoiceNumber: ocrData.invoice_number as string || '',
    issueDate: ocrData.invoice_date as string || new Date().toISOString().split('T')[0],
    dueDate: ocrData.due_date as string || undefined,
    totalHT: Number(ocrData.total_ht || ocrData.totalHt || 0),
    taxRate: Number(ocrData.tva_rate || ocrData.tvaRate || 20),
    totalTTC: Number(ocrData.total_ttc || ocrData.totalTtc || 0) || undefined,
    lineItems: extractLineItems(ocrData),
    status: 'PAID',
  };
}

interface MigrateInvoicesStepProps {
  onComplete: () => void;
  onSkip?: () => void;
}

export function MigrateInvoicesStep({ onComplete, onSkip }: MigrateInvoicesStepProps) {
  const { t } = useTranslation();
  const batchOCR = useBatchOCR();
  const batchImport = useBatchImportInvoices();
  const review = useBatchReview<BatchInvoiceData>(batchOCR.results, extractInvoiceData);

  const inputClasses = 'w-full px-3 py-2 bg-white border border-gray-200 rounded-xl text-sm text-gray-900 focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500 transition-all';

  const hasResults = batchOCR.results.length > 0;

  const handleImportAll = useCallback(() => {
    const items = review.getValidatedItems();
    if (items.length === 0) return;
    batchImport.mutate(items, {
      onSuccess: () => { onComplete(); batchOCR.reset(); },
    });
  }, [review, batchImport, onComplete, batchOCR]);

  return (
    <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="p-3 bg-gradient-to-r from-teal-100 to-teal-200 rounded-xl">
          <Upload className="w-6 h-6 text-teal-600" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-900">
            {t('onboarding.steps.migrate.title', 'Migrer vos factures')}
          </h2>
          <p className="text-sm text-gray-500 mt-0.5">
            {t('onboarding.steps.migrate.subtitle', 'Importez vos anciennes factures depuis des PDF (optionnel)')}
          </p>
        </div>
      </div>

      {/* Upload ou Review */}
      {!hasResults ? (
        <div className="space-y-4">
          <BatchUploadZone
            onFilesSelected={batchOCR.processFiles}
            isProcessing={batchOCR.isProcessing}
            progress={batchOCR.progress}
            total={batchOCR.total}
          />
        </div>
      ) : (
        <BatchReviewLayout<BatchInvoiceData>
          stats={review.stats}
          items={review.items}
          isCreating={batchImport.isPending}
          onBack={() => batchOCR.reset()}
          onValidateAllPending={review.validateAllPending}
          onCreateAll={handleImportAll}
          createLabel={t('invoices.batchImport.importN', 'Importer {{count}} facture(s)', { count: review.stats.validated })}
          onValidate={review.validateItem}
          onReject={review.rejectItem}
          onEdit={review.editItem}
          onConfirmEdit={review.confirmEdit}
          onCancelEdit={review.cancelEdit}
          renderCardContent={(item, index, isEditing) => {
            const data = item.editedData;
            return (
              <>
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-gray-400" />
                  {isEditing ? (
                    <input type="text" value={data.clientName || ''} onChange={(e) => review.updateField(index, 'clientName', e.target.value)} className={inputClasses} placeholder="Client" />
                  ) : (
                    <span className="text-sm text-gray-900 font-medium">{data.clientName || 'Client inconnu'}</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Hash className="w-4 h-4 text-gray-400" />
                  {isEditing ? (
                    <input type="text" value={data.invoiceNumber || ''} onChange={(e) => review.updateField(index, 'invoiceNumber', e.target.value)} className={inputClasses} placeholder="N° facture" />
                  ) : (
                    <span className="text-sm text-gray-600">{data.invoiceNumber || 'N° non détecté'}</span>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-center gap-2">
                    <Euro className="w-4 h-4 text-gray-400" />
                    {isEditing ? (
                      <input type="number" step="0.01" value={data.totalHT} onChange={(e) => review.updateField(index, 'totalHT', parseFloat(e.target.value) || 0)} className={inputClasses} />
                    ) : (
                      <span className="text-sm font-semibold text-gray-900">{new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(data.totalHT)} HT</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    {isEditing ? (
                      <input type="date" value={data.issueDate} onChange={(e) => review.updateField(index, 'issueDate', e.target.value)} className={inputClasses} />
                    ) : (
                      <span className="text-sm text-gray-600">{new Date(data.issueDate).toLocaleDateString('fr-FR')}</span>
                    )}
                  </div>
                </div>
              </>
            );
          }}
        />
      )}

      {/* Actions */}
      {!hasResults && (
        <div className="flex gap-3 pt-6 border-t border-gray-100 mt-6">
          {onSkip && (
            <button
              onClick={onSkip}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-xl transition-all"
            >
              <SkipForward className="w-4 h-4" />
              {t('onboarding.steps.migrate.skip', 'Passer cette étape')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
